import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { fileService, procedureEvidenceService } from '../../../services/auditService';
// fileService also exposes .open(fileId) — see below for the default view action.
import {
  resolveEvidenceAccept,
  fileMatchesEvidenceType,
} from './evidenceTypes';
import EvidenceModal from './EvidenceModal';

/**
 * EvidenceCell — shared per-row + general/overall evidence control (Phase 107 D-3).
 *
 * Used BOTH:
 *   - per-row (scope='row') inside the unified Actions column of each body, and
 *   - general/overall (scope='general') in header Section 2 (Plan 02).
 *
 * It honors the template-authored `evidenceType` (D-3): the file picker `accept=`
 * is constrained to the matching MIME set and a "Expected: {label}" hint is shown.
 * Null / 'None' / unknown types degrade to attachment_type 'any' (no constraint,
 * no hint). Uploads ALWAYS go through fileService.upload — never the raw HTTP
 * client directly.
 *
 * SECURITY (threat T-107-01): the accept= filter + mismatch rejection are UX only.
 * Server-side validation at /api/v1/utl/files remains authoritative.
 */

export interface EvidenceCellProps {
  /** 'row' = per-row Actions column affordance; 'general' = instance-wide. */
  scope: 'row' | 'general';
  /** Template-authored evidence_type; null/'None'/unknown => 'any' degrade. */
  evidenceType?: string | null;
  /** Tenant entity id, passed straight to fileService.upload. */
  entityId: number;
  /** Acting user id, passed straight to fileService.upload. */
  createdBy: number;
  /** Name of an already-attached file (drives the attached-count badge + view action). */
  attachedFileName?: string | null;
  /** Id of the already-attached file. When set and no `onView` is supplied, clicking
   *  an attached cell opens the file in a new tab via fileService.open. */
  attachedFileId?: number | null;
  /** Number of attached evidences (defaults to 1 when attachedFileName is set, else 0). */
  attachedCount?: number;
  /** Called with the uploaded file id on a successful upload. Optional in popup mode
   *  (the popup persists into adt_procedure_evidence itself). */
  onUploaded?: (fileId: number) => void;
  /** Called when the user clicks the icon while evidence is already attached — show the list.
   *  If omitted but `attachedFileId` is set, the cell opens the file itself. */
  onView?: () => void;
  /** Optional disabled state (e.g. read-only workflow). */
  disabled?: boolean;

  // --- Popup mode (multi-evidence per question) -----------------------------
  // When `procedureInstanceId` is provided, the paperclip opens the shared
  // EvidenceModal (list all attachments for this question + upload + view/detach)
  // instead of the legacy single-file picker. This is the surface used across all
  // procedures. itemTypeId/itemRecordId are the SAME ids the body uses for findings.
  /** Enables popup mode when set. */
  procedureInstanceId?: number | null;
  itemTypeId?: number | null;
  itemRecordId?: number | null;
  /** Question text shown for context in the popup header. */
  questionLabel?: string | null;
  /**
   * Whether to show a numeric count badge on the icon.
   * Defaults true (preserves existing callers). Pass false from inline action
   * clusters (Phase 113 REQ-7) to suppress the badge.
   */
  showCount?: boolean;
  /**
   * Called after a successful upload or detach in the evidence popup,
   * so the parent body can re-fetch its inline evidence map.
   */
  onChanged?: () => void;
}

const EvidenceCell: React.FC<EvidenceCellProps> = ({
  scope,
  evidenceType,
  entityId,
  createdBy,
  attachedFileName,
  attachedFileId,
  attachedCount,
  onUploaded,
  onView,
  disabled = false,
  procedureInstanceId,
  itemTypeId,
  itemRecordId,
  questionLabel,
  showCount = true,
  onChanged,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const popupMode = procedureInstanceId != null;

  // Popup mode shows the count of files attached to this question on the paperclip.
  // Fetched once on mount and refreshed whenever the popup closes (after add/remove).
  const [popupCount, setPopupCount] = useState<number | null>(null);
  const refreshCount = useCallback(async () => {
    if (procedureInstanceId == null) return;
    try {
      const list = await procedureEvidenceService.list(
        procedureInstanceId, itemTypeId ?? null, itemRecordId ?? null,
      );
      setPopupCount(list.length);
    } catch { /* non-fatal — badge just won't show */ }
  }, [procedureInstanceId, itemTypeId, itemRecordId]);
  useEffect(() => { refreshCount(); }, [refreshCount]);

  const { accept, label } = resolveEvidenceAccept(evidenceType);

  const openPicker = useCallback(() => {
    if (disabled || uploading) return;
    setError(null);
    inputRef.current?.click();
  }, [disabled, uploading]);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Allow re-selecting the same file later.
      e.target.value = '';
      if (!file) return;

      if (!fileMatchesEvidenceType(file, evidenceType)) {
        setError(`This procedure expects a ${label}. Choose a matching file.`);
        return;
      }

      setError(null);
      setUploading(true);
      try {
        const uploaded = await fileService.upload(file, entityId, createdBy);
        onUploaded?.(uploaded.id);
      } catch {
        setError('Upload failed. Try again.');
      } finally {
        setUploading(false);
      }
    },
    [evidenceType, label, entityId, createdBy, onUploaded],
  );

  const count = popupMode
    ? (popupCount ?? 0)
    : attachedCount != null ? attachedCount : (attachedFileName || attachedFileId) ? 1 : 0;
  const hasEvidence = count > 0;

  // View action: an explicit onView wins; otherwise, if we know the file id, open it
  // ourselves (new tab via the authed client). This is what makes attached evidence
  // actually viewable from every body without each one wiring a handler.
  const viewFn =
    onView ?? (attachedFileId ? () => { void fileService.open(attachedFileId); } : undefined);

  // Tooltip carries the state + the expected-type hint, so the row stays icon-only
  // and uncluttered (no inline "Expected:" / "No evidence attached" text).
  const tooltip = popupMode
    ? 'Evidence — view & attach'
    : uploading
      ? 'Uploading…'
      : hasEvidence
        ? viewFn
          ? `View evidence (${count})`
          : `Evidence attached (${count})`
        : `Attach evidence${label ? ` — expects ${label}` : ''}`;

  const handleClick = useCallback(() => {
    if (uploading) return;
    // Popup mode — the paperclip always opens the multi-evidence popup (list +
    // upload + view/detach). Works in read-only too (the popup hides upload/detach).
    if (popupMode) { setPopupOpen(true); return; }
    // Legacy single-file: viewing attached evidence is allowed even when read-only;
    // only uploading is gated by `disabled`.
    if (hasEvidence && viewFn) { viewFn(); return; }
    if (disabled) return;
    openPicker();
  }, [popupMode, disabled, uploading, hasEvidence, viewFn, openPicker]);

  // Keep the button clickable for the view action even when read-only; block it only
  // when uploading, or when disabled AND there is nothing to view. Popup mode is
  // always clickable (the popup itself enforces read-only on writes).
  const buttonDisabled = popupMode ? uploading : uploading || (disabled && !(hasEvidence && viewFn));

  return (
    <span className="response-evidence-cell">
      <button
        type="button"
        className="response-row-evidence response-row-evidence--icon"
        onClick={handleClick}
        disabled={buttonDisabled}
        title={tooltip}
        aria-label={tooltip}
        data-testid={
          scope === 'general' ? 'BTN_response_shell_evidence' : 'BTN_response_row_evidence'
        }
      >
        <Paperclip size={14} />
        {showCount && hasEvidence && (
          <span className="response-action-count response-action-count--corner">{count}</span>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        hidden
        data-testid="INPUT_evidence_file"
      />

      {error && <span className="response-inline-warning">{error}</span>}

      {popupMode && (
        <EvidenceModal
          isOpen={popupOpen}
          onClose={() => { setPopupOpen(false); refreshCount(); }}
          procedureInstanceId={procedureInstanceId as number}
          itemTypeId={itemTypeId ?? null}
          itemRecordId={itemRecordId ?? null}
          entityId={entityId}
          currentUserId={createdBy}
          questionLabel={questionLabel}
          readOnly={disabled}
          onChanged={onChanged}
        />
      )}
    </span>
  );
};

export default EvidenceCell;
export { EvidenceCell };
